// /src/components/OnePage/OnePage-StatusField.js

import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import { Warning } from "@material-ui/icons";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { validateOnePageStatus } from "../../services/statusValidationApi";
import AuthContext from "../../contexts/AuthProvider.js";

const OnePageStatusField = ({
  value,
  onChange,
  initiativeId,
  onePageId,
  classes,
  t,
}) => {
  const { userDetails } = useContext(AuthContext);
  const [statusWarning, setStatusWarning] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const statusOptions = [
    {
      value: "green",
      label: t("initiative_selected.green"),
      color: "#4caf50",
    },
    {
      value: "yellow",
      label: t("initiative_selected.yellow"),
      color: "#ffbb00",
    },
    {
      value: "red",
      label: t("initiative_selected.red"),
      color: "#E04F5F",
    },
    {
      value: "gray",
      label: t("initiative_selected.gray"),
      color: "#9e9e9e",
    },
  ];

  const validateStatus = async (
    statusValue,
    initiativeIdValue,
    onePageIdValue,
  ) => {
    // Não validar se a funcionalidade estiver desabilitada
    if (userDetails?.has_status_warning === false) {
      setStatusWarning(null);
      return;
    }

    if (!statusValue || !initiativeIdValue) return;

    setIsValidating(true);
    try {
      const response = await validateOnePageStatus(
        initiativeIdValue,
        onePageIdValue,
        statusValue,
      );

      // console.log("API Response:", response); // Debug

      if (response.success && response.hasWarning) {
        // Estrutura correta baseada no backend
        setStatusWarning({
          message: response.warningMessage,
          details: response.warningDetails,
          calculatedStatus: response.calculatedStatus,
        });
      } else {
        setStatusWarning(null);
      }
    } catch (error) {
      // console.error("Erro na validação:", error);
      setStatusWarning(null);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    validateStatus(value, initiativeId, onePageId);
  }, [value, initiativeId, onePageId]);

  const handleStatusChange = (event) => {
    const newValue = event.target.value;
    onChange(newValue);
  };

  // Função para gerar mensagem de warning baseada nos detalhes
  const getWarningMessage = () => {
    if (!statusWarning || !statusWarning.details) return "";

    const { details } = statusWarning;
    const statusLabel = t(
      `initiative_selected.${statusWarning.calculatedStatus}`,
    );

    // Determina o tipo de warning baseado nos detalhes
    const hasRedDeliverables =
      details.red_deliverables && details.red_deliverables.length > 0;
    const hasYellowDeliverables =
      details.yellow_deliverables && details.yellow_deliverables.length > 0;
    const hasRedRisks = details.red_risks && details.red_risks.length > 0;
    const hasYellowRisks =
      details.yellow_risks && details.yellow_risks.length > 0;

    // Determina a mensagem baseada no status calculado (prioridade: vermelho > amarelo > verde)
    if (statusWarning.calculatedStatus === "red") {
      // Para status vermelho, prioriza riscos vermelhos sobre entregáveis vermelhos
      if (hasRedRisks) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.due_to_red_high_risks")}`;
      } else if (hasRedDeliverables) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.due_to_red_deliverables")}`;
      } else {
        // Caso combinado (entregáveis amarelos + riscos vermelhos) - sempre mostra riscos vermelhos
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.due_to_red_high_risks")}`;
      }
    } else if (statusWarning.calculatedStatus === "yellow") {
      // Para status amarelo, prioriza entregáveis amarelos sobre riscos amarelos
      if (hasYellowDeliverables) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.due_to_yellow_deliverables")}`;
      } else if (hasYellowRisks) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.due_to_yellow_high_risks")}`;
      } else {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.based_on_initiative_rules")}`;
      }
    } else {
      return `${t("status_validation.status_should_be")} ${statusLabel} ${t("status_validation.based_on_initiative_rules")}`;
    }
  };

  const getTooltipContent = () => {
    if (!statusWarning) return "";

    const reportedLabel = t(`initiative_selected.${value}`);
    const calculatedLabel = t(
      `initiative_selected.${statusWarning.calculatedStatus}`,
    );
    const reason = getWarningMessage();

    return (
      <span>
        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
          {t("status_validation.warning_title") || "Aviso de Status"}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.reported_status")}:
          </span>{" "}
          {reportedLabel}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.calculated_status")}:
          </span>{" "}
          {calculatedLabel}
        </div>

        <div style={{ marginBottom: "12px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.reason")}:
          </span>{" "}
          {reason}
        </div>

        {/* Detalhes específicos */}
        {statusWarning.details?.red_deliverables?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", color: "#E04F5F" }}>
              {t("status_validation.red_deliverables")}:
            </span>
            {statusWarning.details.red_deliverables.map((del, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {del.name}
              </div>
            ))}
          </div>
        )}

        {statusWarning.details?.yellow_deliverables?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", color: "#ffbb00" }}>
              {t("status_validation.yellow_deliverables")}:
            </span>
            {statusWarning.details.yellow_deliverables.map((del, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {del.name}
              </div>
            ))}
          </div>
        )}

        {statusWarning.details?.red_risks?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", color: "#E04F5F" }}>
              {t("status_validation.red_high_risks")}:
            </span>
            {statusWarning.details.red_risks.map((risk, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {risk.name}
              </div>
            ))}
          </div>
        )}

        {statusWarning.details?.yellow_risks?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", color: "#ffbb00" }}>
              {t("status_validation.yellow_high_risks")}:
            </span>
            {statusWarning.details.yellow_risks.map((risk, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {risk.name}
              </div>
            ))}
          </div>
        )}
      </span>
    );
  };

  const renderValue = (selected) => {
    const status = statusOptions.find((s) => s.value === selected);
    if (!status) return "";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <div
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: status.color,
            flexShrink: 0,
          }}
        />
        <span style={{ color: "#777" }}>{status.label}</span>

        {/* Ícone de warning se houver e se a funcionalidade estiver habilitada */}
        {statusWarning && userDetails?.has_status_warning !== false && (
          <Tippy
            content={getTooltipContent()}
            theme="light"
            arrow={true}
            placement="top"
            interactive={true}
            maxWidth={400}
          >
            <Warning
              style={{
                color: "#ff9800",
                fontSize: "16px",
                marginLeft: "auto",
                cursor: "pointer",
              }}
            />
          </Tippy>
        )}
      </div>
    );
  };

  // Debug: mostrar o estado do warning
  // console.log("StatusWarning state:", statusWarning);

  return (
    <FormControl
      style={{ width: "100%", marginBottom: "16px" }}
      className={`${classes?.selectFormControl || ""} ${classes?.selectFormControlOverride || ""}`}
    >
      <InputLabel id="new-status-label" style={{ color: "#888" }}>
        {t("initiative_selected.one_page_status")}
        {statusWarning && userDetails?.has_status_warning !== false && (
          <span style={{ color: "#ff9800", marginLeft: "4px" }}>*</span>
        )}
      </InputLabel>

      <Select
        labelId="new-status-label"
        value={value || ""}
        onChange={handleStatusChange}
        label="Status"
        displayEmpty
        disabled={isValidating}
        classes={{
          select: `${classes?.select || ""} ${classes?.selectFormControlOverride || ""}`,
        }}
        renderValue={renderValue}
      >
        {statusOptions.map((status) => (
          <MenuItem
            key={status.value}
            value={status.value}
            classes={{
              root: classes?.selectMenuItem || "",
              selected: classes?.selectMenuItemSelected || "",
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: status.color,
                border: "0.2px solid white",
                flexShrink: 0,
              }}
            />
            <span>{status.label}</span>
          </MenuItem>
        ))}
      </Select>

      {/* Mensagem de warning abaixo do campo */}
      {statusWarning && userDetails?.has_status_warning !== false && (
        <div
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#ff9800",
            display: "flex",
            alignItems: "flex-start",
            gap: "4px",
            width: "100%",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            lineHeight: "1.4",
          }}
        >
          <Warning
            style={{
              fontSize: "14px",
              flexShrink: 0,
              marginTop: "1px",
            }}
          />
          <span style={{ flex: 1, minWidth: 0 }}>
            {getWarningMessage() ||
              t("status_validation.status_different_from_rules")}
          </span>
        </div>
      )}
    </FormControl>
  );
};

OnePageStatusField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  initiativeId: PropTypes.string.isRequired,
  onePageId: PropTypes.string,
  classes: PropTypes.object,
  t: PropTypes.func.isRequired,
};

export default OnePageStatusField;
